/**
 * Importação de lotes a partir de planilha Excel (Programa de Produção).
 * Colunas: Produto, N°Lote, Qtd, Corte… (início), Embalagem (fim).
 * Cor e código vêm do nome do produto — não entram na pontuação.
 */

import * as XLSX from 'xlsx';
import {
  normalizarChaveProduto,
  parseProdutoCelulaExcel,
} from './resolverProdutoPontuacao';

export { parseProdutoCelulaExcel } from './resolverProdutoPontuacao';

export interface LinhaLoteExcelPreview {
  id: string;
  selecionada: boolean;
  lote: string;
  produtoNome: string;
  corProduto?: string;
  codigoProduto?: string;
  qtd: number;
  dataHoraInicioCorte?: string;
  dataHoraFimEmbalagem?: string;
  textoProdutoOriginal?: string;
  encontradoCatalogo: boolean;
  aviso?: string;
}

export interface ResultadoParseLotesExcel {
  linhas: LinhaLoteExcelPreview[];
  avisos: string[];
}

type ColunaId = 'produto' | 'lote' | 'qtd' | 'inicio' | 'fim';

/** Prefixos para detectar célula de produto na planilha (não remover do nome). */
const PREFIXOS_TIPO_PRODUTO = [
  'estante',
  'rack',
  'buffet',
  'aparador',
  'mesa',
  'painel',
  'armario',
  'balcao',
  'gaveteiro',
  'cristaleira',
  'comoda',
  'cômoda',
  'guarda roupa',
  'guarda-roupa',
  'livreiro',
  'nicho',
];

function normalizarColuna(s: string): string {
  return normalizarChaveProduto(s).replace(/[°ºª]/g, '').replace(/\s+/g, ' ').trim();
}

function celulaParaTexto(val: unknown): string {
  if (val == null || val === '') return '';
  if (val instanceof Date) {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(val.getDate())}/${pad(val.getMonth() + 1)}/${val.getFullYear()}`;
  }
  return String(val).trim();
}

function classificarColuna(header: string): ColunaId | null {
  const t = normalizarColuna(header);
  if (!t) return null;
  if (t.includes('PRODUTO') || t.includes('DESCRIC')) return 'produto';
  if (t.includes('LOTE') || t.includes('NLOTE') || /^N\s*LOTE$/.test(t)) return 'lote';
  if (t.includes('QTD') || t.includes('QUANT')) return 'qtd';
  if (t.includes('EMBALAGEM')) return 'fim';
  if (t.includes('CORTE') || t.includes('COLADEIRA') || (t.includes('FURADEIRA') && t.includes('MDF'))) {
    return 'inicio';
  }
  return null;
}

function pareceCelulaProduto(texto: string): boolean {
  const t = texto.trim();
  if (t.length < 8 || /^produto$/i.test(t)) return false;
  const temPrefixo = PREFIXOS_TIPO_PRODUTO.some((p) => new RegExp(`\\b${p}\\b`, 'i').test(t));
  const temCodigo = /\d{4,}\s*$/.test(t);
  return temPrefixo || (temCodigo && /[a-záàâãéêíóôõúç]/i.test(t));
}

function parseDataExcel(val: unknown): string | undefined {
  if (val == null || val === '' || val === '-') return undefined;

  if (val instanceof Date && !isNaN(val.getTime())) {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${val.getFullYear()}-${pad(val.getMonth() + 1)}-${pad(val.getDate())}T07:00`;
  }

  if (typeof val === 'number' && val > 30000 && val < 60000) {
    const parsed = XLSX.SSF.parse_date_code(val);
    if (parsed) {
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${parsed.y}-${pad(parsed.m)}-${pad(parsed.d)}T07:00`;
    }
  }

  const s = String(val).trim();
  const m = s.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (m) {
    let [, d, mo, y] = m;
    if (y.length === 2) y = `20${y}`;
    return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}T07:00`;
  }

  const iso = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}T07:00`;

  return undefined;
}

function parseQtd(val: unknown): number {
  const n = parseInt(String(val ?? '').replace(/\D/g, ''), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function parseLote(val: unknown): string {
  const s = String(val ?? '').trim();
  const nums = s.match(/\d{3,6}/);
  return nums ? nums[0] : '';
}

function ehLinhaPrograma(row: unknown[]): boolean {
  const txt = row.map(celulaParaTexto).join(' ');
  const n = normalizarChaveProduto(txt);
  return n.includes('PROGRAMA DE PRODUCAO') || n.includes('PROGRAMA DE PROD');
}

function ehLinhaCabecalho(row: unknown[]): boolean {
  const cols = row.map((c) => normalizarColuna(celulaParaTexto(c)));
  const temLote = cols.some((c) => c.includes('LOTE') || c.includes('NLOTE'));
  const temProduto = cols.some((c) => c.includes('PRODUTO'));
  const temQtd = cols.some((c) => c.includes('QTD'));
  return temLote && (temProduto || temQtd);
}

function mapearColunas(headerRow: unknown[]): Map<ColunaId, number> {
  const mapa = new Map<ColunaId, number>();
  headerRow.forEach((h, idx) => {
    const tipo = classificarColuna(celulaParaTexto(h));
    if (tipo && !mapa.has(tipo)) mapa.set(tipo, idx);
  });
  if (!mapa.has('produto') && mapa.has('lote')) {
    const loteIdx = mapa.get('lote')!;
    mapa.set('produto', Math.max(0, loteIdx - 1));
  }
  return mapa;
}

function linhaVazia(row: unknown[]): boolean {
  return row.every((c) => !celulaParaTexto(c));
}

function acharProdutoNaLinha(row: unknown[], mapa: Map<ColunaId, number>): string {
  if (mapa.has('produto')) {
    const p = celulaParaTexto(row[mapa.get('produto')!]);
    if (pareceCelulaProduto(p)) return p;
  }
  for (const c of row) {
    const t = celulaParaTexto(c);
    if (pareceCelulaProduto(t)) return t;
  }
  return '';
}

function parsePlanilha(rows: unknown[][], nomesPontuacao: readonly string[]): LinhaLoteExcelPreview[] {
  const out: LinhaLoteExcelPreview[] = [];
  let mapa: Map<ColunaId, number> | null = null;
  let lotePadrao = '';

  for (const row of rows) {
    if (!row || !Array.isArray(row) || linhaVazia(row)) continue;

    if (ehLinhaPrograma(row)) {
      mapa = null;
      lotePadrao = '';
      continue;
    }

    if (ehLinhaCabecalho(row)) {
      mapa = mapearColunas(row);
      continue;
    }

    if (!mapa || !mapa.has('lote')) continue;

    const produtoRaw = acharProdutoNaLinha(row, mapa);
    if (!produtoRaw) continue;

    const loteRaw = mapa.has('lote') ? celulaParaTexto(row[mapa.get('lote')!]) : '';
    const qtdRaw = mapa.has('qtd') ? row[mapa.get('qtd')!] : '';
    const inicioRaw = mapa.has('inicio') ? row[mapa.get('inicio')!] : '';
    const fimRaw = mapa.has('fim') ? row[mapa.get('fim')!] : '';

    const lote = parseLote(loteRaw) || lotePadrao;
    const qtd = parseQtd(qtdRaw);
    if (!lote || !qtd) continue;
    if (loteRaw) lotePadrao = lote;

    const parsed = parseProdutoCelulaExcel(produtoRaw, nomesPontuacao);
    out.push({
      id: `${out.length}-${Date.now()}`,
      selecionada: true,
      lote,
      qtd,
      dataHoraInicioCorte: parseDataExcel(inicioRaw),
      dataHoraFimEmbalagem: parseDataExcel(fimRaw),
      aviso: !parsed.encontradoCatalogo ? 'Produto não encontrado na lista de pontuação' : undefined,
      ...parsed,
    });
  }

  return out;
}

export async function parseLotesExcelArquivo(
  file: File,
  nomesPontuacao: readonly string[]
): Promise<ResultadoParseLotesExcel> {
  const avisos: string[] = [];
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array', cellDates: true });
  const todasLinhas: LinhaLoteExcelPreview[] = [];
  const vistos = new Set<string>();

  for (const nomeAba of wb.SheetNames) {
    const ws = wb.Sheets[nomeAba];
    if (!ws) continue;
    const rows = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, defval: '', raw: false });
    const parsed = parsePlanilha(rows, nomesPontuacao);
    for (const l of parsed) {
      const chave = `${l.lote}|${normalizarChaveProduto(l.produtoNome)}|${normalizarChaveProduto(l.corProduto ?? '')}|${l.qtd}|${l.codigoProduto ?? ''}`;
      if (vistos.has(chave)) continue;
      vistos.add(chave);
      todasLinhas.push({ ...l, id: `${todasLinhas.length}-${Date.now()}` });
    }
  }

  if (todasLinhas.length === 0) {
    avisos.push('Nenhuma linha de produto encontrada. Confira se a planilha tem colunas Produto, N°Lote e Qtd.');
    avisos.push('Dica: exporte o Programa de Produção para Excel (.xlsx) mantendo os títulos das colunas.');
  }

  return { linhas: todasLinhas, avisos };
}
